import * as React from 'react';
import type { TextOnPathProps } from '../../TextOnPath.types';
import type { Measurement } from '../useSvgMeasurement';
import { ConditionalLink } from './ConditionalLink';
import styles from '../TextOnPath.scss';
import { useTextOnPathCalculations } from '../hooks/useTextOnPathCalculations';
import { ViewerTestIds } from '../../constants';

type SvgTextProps = React.ComponentProps<'text'> & {
  pathId: string;
  textLength?: number;
  children: React.ReactNode;
  startOffset?: string;
  from?: string;
  to?: string;
  duration?: number;
};

function TextElement({
  pathId,
  textLength,
  children,
  startOffset,
  from,
  to,
  duration,
  ...restProps
}: SvgTextProps) {
  const textPathRefCallback = React.useCallback(
    (textPathElement: SVGTextPathElement | null) => {
      if (!(textPathElement && from && to && duration && duration > 0)) {
        return;
      }
      requestAnimationFrame(() => {
        const animate = document.createElementNS(
          'http://www.w3.org/2000/svg',
          'animate',
        );
        animate.setAttributeNS(null, 'attributeName', 'startOffset');
        animate.setAttributeNS(null, 'from', from);
        animate.setAttributeNS(null, 'to', to);
        animate.setAttributeNS(null, 'dur', `${duration}s`);
        animate.setAttributeNS(null, 'repeatCount', 'indefinite');
        animate.setAttributeNS(null, 'begin', '0s');

        textPathElement.appendChild(animate);
      });
    },
    [from, to, duration],
  );

  return (
    <text {...restProps}>
      <textPath
        ref={textPathRefCallback}
        href={`#${pathId}`}
        textLength={textLength}
        startOffset={startOffset}
      >
        {children}
      </textPath>
    </text>
  );
}

export type TextOnPathSvgProps = Omit<TextOnPathProps, 'translations'> & {
  measurement: Measurement | undefined;
  isAnimated: boolean;
  startOffset?: number;
};

export function TextOnPathSvg({
  text,
  id,
  path,
  viewBox,
  speed,
  fillMode,
  movementDirection,
  pathLength,
  measurement,
  isAnimated,
  link,
  isDecorative,
  tagName,
  startOffset = 0,
}: TextOnPathSvgProps) {
  const {
    textWidthMultiplied,
    animationOffsets: { from1, to1, from2, to2 },
    duration,
    repeatedText,
    effectiveStartOffset,
  } = useTextOnPathCalculations({
    fillMode,
    pathLength,
    measurement,
    movementDirection,
    speed,
    text,
    startOffset,
  });

  const isJustifyMode = fillMode === 'justify';
  const zeroWidthSpace = isAnimated ? <>&#x200B;</> : null;
  const textLength = !isJustifyMode ? undefined : textWidthMultiplied;
  const pathIdWithPrefix = `${id}-path`;
  const textGroupId = `${id}-text`;
  const Tag = tagName ? (tagName as keyof JSX.IntrinsicElements) : null;

  const TextOnPathSvgComponent = () => (
    <svg
      className={styles.textOnPathRootSvg}
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid meet"
      viewBox={viewBox}
      aria-labelledby={!isDecorative ? textGroupId : undefined}
      aria-hidden={isDecorative || undefined}
      data-svg-ref="true"
    >
      <path
        id={pathIdWithPrefix}
        d={path}
        pathLength={pathLength}
        data-testid={ViewerTestIds.path}
      />
      <TextElement
        pathId={pathIdWithPrefix}
        textLength={textLength}
        startOffset={isAnimated ? undefined : `${effectiveStartOffset}%`}
        from={from1}
        to={to1}
        duration={duration}
        id={textGroupId}
        aria-hidden={isDecorative}
        data-testid={ViewerTestIds.text}
      >
        {repeatedText}
        {zeroWidthSpace}
      </TextElement>
      {isAnimated && (
        <TextElement
          pathId={pathIdWithPrefix}
          textLength={textLength}
          from={from2}
          to={to2}
          duration={duration}
          aria-hidden="true"
        >
          {repeatedText}
          {zeroWidthSpace}
        </TextElement>
      )}
    </svg>
  );

  return (
    <ConditionalLink link={link}>
      {isDecorative || !Tag ? (
        <TextOnPathSvgComponent />
      ) : (
        <Tag className={styles.textOnPathSemanticTag}>
          <TextOnPathSvgComponent />
        </Tag>
      )}
    </ConditionalLink>
  );
}
